import DiscoveryFollowingTab from "../../components/DiscoveryFollowingTab";

import { AD_POSITION } from "@/constants/ads";
import { CACHEOR_KEYS } from "@/constants/cacheor";
import { SORT, SORT_ITEM_MAP } from "@/constants/discoverySort";
import useAppStore from "@/context/store";
import DiscoveryFeaturePinList from "@/pageComponents/DiscoveryPinList/feature";
import logger from "@/server/logger";
import {
  getAds,
  getCategories,
  getDiscoveryBoards,
  getDiscoveryPins,
  getDiscoveryRecommend,
} from "@/services";
import { getAiGameplayList, sortAiGameplayList } from "@/services/ai-gameplay";
import { getCalendarList, getSeoInlinks } from "@/services/qingzhou";
import { clearSearch } from "@/tracker/shared/global";
import { isSafari } from "@/utils/isSafari";
import { safeParse } from "@/utils/safeParse";
import { cacheor } from "@/utils/server/cacheor";
import { isLogined } from "@/utils/server/isLogined";
import { serverSideRedirect } from "@/utils/server/serverSide";
import { flatten, parseInt, random } from "lodash";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import styles from "./index.module.less";

function randomPinPreivew(calendar) {
  const pins = flatten(
    calendar.filters.map((item) => item.children?.[0]?.preview?.pins)
  ).filter((item) => item);

  /**随机预览封面 */
  const coverPin = pins[random(0, pins.length - 1)];

  return coverPin?.file;
}

/**处理日历数据： 随机封面，过滤数据 */
function handleCalendars(data) {
  try {
    return (data || [])
      .map((item) => {
        return {
          title: item.title,
          code: item.code,
          id: item.id,
          preview_pin: randomPinPreivew(item),
          /**热度 */
          top_priority: item.top_priority,
          // 时间
          festival_time: item.festival_time || null,
          festival_end_time: item.festival_end_time || null,
          start_time: item.start_time || null,
          end_time: item.end_time || null,
          // 日期类型
          date_type: item.date_type,
        };
      })
      .filter((item) => item.preview_pin);
  } catch (error) {
    logger.error("handleCalendars error", error);
    return [];
  }
}

async function getCalendarData(query) {
  // 没有广告再请求日历信息
  const cache = await cacheor.get({ key: CACHEOR_KEYS.DISCOVERY_CALENDARS });

  if (cache && !query?._cache) {
    return handleCalendars(cache.length ? cache : []);
  } else {
    const calendarListRes = await getCalendarList(
      {
        page_num: 1,
        page_size: 20,
        load_filters: 1,
        fields: "CALENDAR",
        preview_limit: 6,
      },
      { ignoreError: true }
    );

    if (calendarListRes.data) {
      // 凌晨0点时间
      const midnight = new Date();
      midnight.setHours(24, 0, 0, 0);

      cacheor.set({
        key: CACHEOR_KEYS.DISCOVERY_CALENDARS,
        value: calendarListRes.data || [],
        ttl: Math.floor((midnight - new Date()) / 1000), // 缓存到晚上0点
      });

      return handleCalendars(
        calendarListRes.data?.length ? calendarListRes.data : []
      );
    }
  }
}

/**
 * 获取核心业务数据（Pins 或 Boards）
 * 根据 tabId 和 sort 参数决定获取哪种类型的列表数据
 *
 * @param {Object} req - Request 对象
 * @param {Object} query - URL 查询参数
 * @param {string} tabId - 当前标签页 ID
 * @param {string} categoryId - 当前分类 ID
 * @returns {Promise<Object>} 包含 pins 或 boards 数据的对象
 */
async function fetchCoreData(req, query, tabId, categoryId) {
  const { sort = 0 } = query;

  // 统一错误处理函数
  const handleError = (type, err) => {
    logger.error(`fetchCoreData ${type} failed`, err);
    return { [`serverside${type}`]: [] };
  };

  if (tabId === "pins") {
    if (+sort === 0) {
      // 推荐流 (Feature Pins)
      const cachePage =
        safeParse(decodeURIComponent(req.cookies.feature_page || "{}"))?.[
          categoryId || "all"
        ] || 1;

      const fetchCategoryId = categoryId === "all" ? null : categoryId;

      return getDiscoveryRecommend({
        // 随机页面，避免未登录用户刷新首屏都是重复的
        page_num: cachePage,
        page_size: 40,
        category: fetchCategoryId,
      })
        .then((result) => ({
          serversideFeaturePins: result?.pins || [],
        }))
        .catch((err) => handleError("FeaturePins", err));
    } else {
      // 普通 Pins 流
      return getDiscoveryPins({
        category: categoryId || "",
        limit: 40,
      })
        .then((result) => ({
          serversidePins: result?.pins || [],
        }))
        .catch((err) => handleError("Pins", err));
    }
  } else {
    // 画板流 (Boards)
    return getDiscoveryBoards({
      category: categoryId || "",
      limit: 40,
    })
      .then((result) => ({
        serversideBoards: result?.boards || [],
      }))
      .catch((err) => handleError("Boards", err));
  }
}

/**
 * 服务端数据预取
 * 采用并行请求策略优化首屏加载速度
 */
export async function getServerSideProps({ req, params, query, res }) {
  let { categoryId = null, tabId = "pins" } = params || {};

  // 素材页重定向
  if (tabId === "materials") {
    return serverSideRedirect(res, {
      destination: `/materials`,
      permanent: true,
    });
  }

  const cookie = req?.headers["cookie"];

  // ==========================================
  // 1. 构建并行请求 Promise
  // ==========================================

  // Group 1: 广告与页面配置
  // 这两个接口影响页面布局和后续请求逻辑 (如日历开关)
  const adsAndConfigPromise = Promise.all([
    getAds({
      positions: [
        // AD_POSITION.DISCOVERY_TOP,
        AD_POSITION.DISCOVERY_WATERFALL_BANNER,
        AD_POSITION.DISCOVERY_WATERFALL_FIXED_1,
        AD_POSITION.DISCOVERY_WATERFALL_FIXED_2,
        AD_POSITION.DISCOVERY_WATERFALL_RANDOM,
        AD_POSITION.DISCOVERY_WATERFALL_4,
      ],
      variables: {
        category: [categoryId === "all" || !categoryId ? "none" : categoryId],
        user_tag: [tabId],
      },
      headers: {
        ...(cookie ? { cookie } : null),
      },
    }).catch((err) => {
      logger.error("getAds failed", err);
      return []; // 广告加载失败降级为无广告
    }),
    // 页面配置，控制日历展示开关
    cacheor
      .getGrocery({
        path: "discovery-page",
        disableCache: query?._cache,
      })
      .catch((err) => {
        logger.error("getGrocery failed", err);
        return {}; // 配置加载失败使用默认值
      }),
  ]);

  // Group 2: 核心业务数据 (Pins / Boards)
  // 这是页面的主要内容，独立于广告和配置
  // 登录用户或JSON请求不请求核心业务数据, 接口太慢，会导致页面切换到发现页时卡顿
  const coreDataPromise =
    isLogined(req) || req.url.includes(".json")
      ? Promise.resolve({})
      : fetchCoreData(req, query, tabId, categoryId);

  // Group 3: SEO 与分类信息
  // 辅助信息，用于 Meta 标签和标题展示
  const seoAndMetaPromise = Promise.all([
    getSeoInlinks({
      title: "最新采集_灵感图片大全_花瓣网",
      current_url: req.url,
    }).catch((err) => {
      logger.error("getSeoInlinks failed", err);
      return []; // SEO 外链失败忽略
    }),
    getCategories().catch((err) => {
      logger.error("getCategories failed", err);
      return {}; // 分类加载失败忽略
    }),
  ]);

  // ==========================================
  // 2. 发起所有独立请求 (Parallel Execution)
  // ==========================================
  const [
    [discoveryAds, pageConf],
    coreDataResult,
    [inlinks, categoriesResult],
  ] = await Promise.all([
    adsAndConfigPromise,
    coreDataPromise,
    seoAndMetaPromise,
  ]);

  // ==========================================
  // 3. 依赖 pageConf 的请求 (Calendar / AI Gameplay)
  // ==========================================
  // 这部分必须等待 pageConf 返回，但不应该阻塞 Core Data 的获取（上面已经并行了）
  const [calendarList, aiGameplayList] = await Promise.all([
    pageConf?.calendarShow
      ? getCalendarData(query).catch((err) => {
          logger.error("getCalendarData failed", err);
          return [];
        })
      : Promise.resolve([]),
    pageConf?.aigameplayShow ? getAiGameplayList() : Promise.resolve([]),
  ]);

  const props = {
    calendarList,
    multiColAd: discoveryAds?.[0] || null, // 确保解构安全
    fixedAd1: discoveryAds?.[1] || null,
    fixedAd2: discoveryAds?.[2] || null,
    randomAd: discoveryAds?.[3] || null,
    fourAd: discoveryAds?.[4] || null,
    randomAdIndex: parseInt(Math.random() * 10) + 1,
    aiGameplayList: sortAiGameplayList(aiGameplayList),
    ...coreDataResult,
    inlinks,
  };

  // 登录用户或JSON请求优化：直接返回 props，不再进行额外的重定向或处理
  // 注意：这里如果需要做登录态校验或其他逻辑，应在此处处理
  if (isLogined(req) || req.url.includes(".json")) {
    return {
      props,
    };
  }

  // 计算 H1 标题描述，用于 SEO
  let h1Desc =
    categoriesResult?.categories?.find((item) => item.id === categoryId)
      ?.name || "推荐";
  props.h1Desc = h1Desc;
  return { props };
}

export default function Discovery({
  serversideFeaturePins,
  h1Desc,
  calendarList,
  multiColAd,
  aiGameplayList,
  fixedAd1,
  fixedAd2,
  randomAd,
  fourAd,
  randomAdIndex,
}) {
  const router = useRouter();
  let { categoryId = null } = router.query;
  const columnNum = useAppStore((s) => s.column);

  const [clientMultiColAd, setClientMultiColAd] = useState(multiColAd);

  // 缓存 aiGameplayList，避免每次渲染创建新的引用导致子组件频繁重新计算
  const stableAiGameplayList = useMemo(() => {
    return aiGameplayList || [];
  }, [aiGameplayList]);

  // 如果localStorage未设置sort就是undefined，这样就走AB的逻辑
  const sortValue = SORT_ITEM_MAP[SORT.ALL];

  const adMergeColNum = useMemo(() => {
    // 规则: 瀑布流: 4-5列, 广告位展示占2列,
    // 6-8列, 广告位展示占3列, 最多3列
    if (columnNum < 6) {
      return 2;
    }

    return 3;
  }, [columnNum]);

  const recommendAds = useMemo(() => {
    // 优先使用客户端获取的广告（Safari浏览器）
    const currentAd = clientMultiColAd;

    if (currentAd) {
      return [
        {
          ...currentAd,
          mergeCol: adMergeColNum,
          pin_id: `recommend-multiColAd-${adMergeColNum}-${
            //卡片key值更新，用于强制更新，解决safari渲染问题
            currentAd.isSafari || ""
          }`,
          type: "recommend",
        },
      ];
    }

    return [];
  }, [adMergeColNum, clientMultiColAd]);

  useEffect(() => {
    clearSearch();

    // 进入发现页面时清空 pinStack，避免返回时跳转到错误的页面
    const { clearPinStack, pinStack } = useAppStore.getState();
    if (pinStack && pinStack.length > 0) {
      clearPinStack();
    }
  }, []);

  // Safari浏览器有渲染兼容性问题，会导致广告的高度计算异常，需要在客户端强制异步更新一下
  useEffect(() => {
    if (isSafari() && multiColAd) {
      const timeout = setTimeout(() => {
        // safari浏览器标识, 用于强制更新，解决渲染问题
        setClientMultiColAd({ ...multiColAd, isSafari: "isSafari" });
      }, 50);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [multiColAd, categoryId]);

  return (
    <div
      className={styles.discovery + " calculateWrapper"}
      data-sort-type={sortValue?.label || ""}
    >
      <Head>
        <title>最新采集_灵感图片大全_花瓣网</title>
      </Head>
      {h1Desc && (
        <div className="golbalHiddenH1">
          <h1>{h1Desc}</h1>
        </div>
      )}
      <DiscoveryFollowingTab />
      <div>
        <DiscoveryFeaturePinList
          categoryId={categoryId}
          initList={serversideFeaturePins}
          sortName={sortValue?.label}
          recommends={recommendAds}
          aiGameplayList={stableAiGameplayList}
          fixedAd1={fixedAd1}
          fixedAd2={fixedAd2}
          randomAd={randomAd}
          fourAd={fourAd}
          randomAdIndex={randomAdIndex}
        />
      </div>
    </div>
  );
}
